import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Loader2, Package, ArrowRight, Sprout } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("success");
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setOrder(response.data);
      } else {
        // Still processing, check again in 2 seconds
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Payment status check failed:", error);
      setStatus("success"); // Assume success if we got here
    }
  };

  const colors = {
    green: { primary: "#2D8B4E", light: "#A8D5BA", dark: "#1A5632" },
    gold: { primary: "#FFB81C", light: "#FFE4A0" }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.green.light }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 text-center max-w-md"
        >
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: colors.green.primary }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: colors.green.dark }}>Processing Payment</h2>
          <p className="text-gray-500">Please wait while we confirm your order...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(180deg, ${colors.green.light}, white)` }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-8 text-center max-w-lg shadow-xl"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
          style={{ background: colors.green.primary }}
        >
          <Check className="w-10 h-10 text-white" />
        </motion.div>

        <h1 className="text-3xl font-bold mb-2" style={{ color: colors.green.dark }}>
          Order Confirmed! 🌱
        </h1>
        <p className="text-gray-600 mb-6">
          Thank you for your purchase! Your seeds are on their way to help you grow something amazing.
        </p>

        {orderId && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-500">Order ID</p>
            <p className="font-mono font-bold" style={{ color: colors.green.primary }}>{orderId}</p>
          </div>
        )}

        <div className="space-y-3 mb-8">
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: colors.gold.light }}>
            <Package className="w-5 h-5" style={{ color: colors.gold.primary }} />
            <span className="text-sm">Free shipping on all orders</span>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: colors.green.light }}>
            <Sprout className="w-5 h-5" style={{ color: colors.green.primary }} />
            <span className="text-sm">Seeds typically ship within 2-3 business days</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={() => navigate("/challenges")}
            className="flex-1 h-12"
            style={{ background: colors.green.primary }}
          >
            Start a Challenge <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Link to="/marketplace" className="flex-1">
            <Button variant="outline" className="w-full h-12">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
